import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {User, UserNotification, UsersService} from '@core/interfaces/common/users';
import {shareReplay, switchMap, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {NbToastrService} from '@nebular/theme';

export enum Org {
    EII,
    ALECTRA,
}

@Injectable()
export class UsersStore {
    public multipleTenants = new BehaviorSubject<boolean>(false);
    public currentUser = new BehaviorSubject<User>(null);
    readonly currentUser$: Observable<User> = this.currentUser.asObservable().pipe(shareReplay(1));

    private requestLoading = new BehaviorSubject<boolean>(false);
    readonly requestLoading$ = this.requestLoading.asObservable().pipe(shareReplay(1));

    constructor(private userService: UsersService, private router: Router, private toastrService: NbToastrService) {}

    /**
     * Get current user.
     */
    public getCurrentUser() {
        return this.currentUser.getValue();
    }

    /**
     * Refresh current user. This will trigger currentUser$ observable.
     */
    refreshUser() {
        return this.userService.getCurrentUser().pipe(tap((user: User) => this.setUser(user)));
    }

    setUser(user: User) {
        // If user timezone is undefined (never set), one-time set based on timezone from browser
        if (!user.timezone) {
            const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const newUserInfo = {
                ...user,
                timezone: localTimezone,
            };
            this.updateUser(newUserInfo);
        } else {
            this.currentUser.next(user);
        }

        // Support for different icons for EII vs. partner/demo organizations
        try {
            const userOrg: Org = this.userOrg(user);
            if (userOrg != this.iconOrg()) {
                const oldLinkPng = document.getElementById('dynamic-favicon-png'),
                    oldLinkIco = document.getElementById('dynamic-favicon-ico'),
                    linkPng = document.createElement('link'),
                    linkIco = document.createElement('link');
                linkPng.id = 'dynamic-favicon-png';
                linkPng.rel = 'icon';
                linkPng.href = this.getFaviconPng(userOrg);
                linkIco.id = 'dynamic-favicon-ico';
                linkIco.rel = 'icon';
                linkIco.href = this.getFaviconIco(userOrg);
                if (oldLinkPng) {
                    document.head.removeChild(oldLinkPng);
                }
                if (oldLinkIco) {
                    document.head.removeChild(oldLinkIco);
                }
                document.head.appendChild(linkPng);
                document.head.appendChild(linkIco);
            }
        } catch (err) {
            // do nothing
        }
    }

    clearCurrentUser() {
        this.currentUser.next(null);
    }

    logout() {
        // Clear all local data, navigate to login page; send session expired message
        localStorage.clear();
        this.clearCurrentUser();

        this.router.navigate(['/auth/login']);
        setTimeout(() => {
            this.toastrService.warning(
                'Session has expired and you have been automatically logged out. ' + 'Please log back in.',
                'Session is expired',
                {duration: 0},
            );
        }, 1500);
    }

    updateUser(newUserInfo) {
        this.userService.update(newUserInfo).subscribe(() => {
            this.currentUser.next(newUserInfo);
        });
    }

    public fromLogin = new BehaviorSubject<boolean>(false);

    readonly getUserNotifications$: Observable<UserNotification[]> = this.fromLogin.asObservable().pipe(
        switchMap((flag) => {
            if (flag) {
                return this.userService.getUsersNotification();
            } else {
                return of(null);
            }
        }),
    );

    public changeRequestLoading(value) {
        this.requestLoading.next(value);
    }

    /*
     * Helpers to support different orgs with logo swap
     */
    private userOrg(user: User): Org {
        if (user && user.username) {
            if (user.username.includes('alectra.') || user.username.includes('.alectra')) return Org.ALECTRA;
            return Org.EII;
        }
        return Org.EII;
    }
    private iconOrg(): Org {
        const favicon = document.getElementById('dynamic-favicon-png');
        if (favicon) {
            if (favicon['href'].includes('alectra')) return Org.ALECTRA;
            return Org.EII;
        }
        return Org.EII;
    }
    private getFaviconPng(org: Org): string {
        switch (org) {
            case Org.ALECTRA:
                return 'assets/favicon/favicon_alectra.png';
            case Org.EII:
            default:
                return 'assets/favicon/favicon.png';
        }
    }
    private getFaviconIco(org: Org): string {
        switch (org) {
            case Org.ALECTRA:
                return 'assets/favicon/favicon_alectra.ico';
            case Org.EII:
            default:
                return 'assets/favicon/favicon.ico';
        }
    }
    public getCurrentOrg(): Org {
        try {
            return this.userOrg(this.currentUser.value);
        } catch (err) {
            return Org.EII;
        }
    }
    public checkCurrentOrg(user: User): Org {
        try {
            return this.userOrg(user);
        } catch (err) {
            return Org.EII;
        }
    }
}
